import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Box, Tabs, Tab, makeStyles } from "@material-ui/core";
import BorrowerInfoTab from "./FinstarComponents/BorrowerInfoTab";
import CollateralTab from "./FinstarComponents/CollateralTab";
import ModalWrapper from "@View/components/ModalWrapper";
import ModalTittle from "@View/components/ModalTittle";
import ButtonBB from "@View/components/buttons/ButtonBB";
import ButtonSave from "@View/components/buttons/ButtonSave";
import getAPI from "@API/index";
import { getRandomKey } from "@Utils/math";
import { AppActions } from "@State/reducers/getReducers/appReducer";

const useStyles = makeStyles((theme) => ({
  tabsContainer: {
    borderBottom: "1px solid #E1E6F1",
    padding: "0 30px",
    flexShrink: 0,
    "& .MuiTabs-indicator": {
      backgroundColor: "#354659",
    },
    "& .MuiTab-root": {
      color: "#54606F",
      fontWeight: 500,
      fontSize: "14px",
      textTransform: "none",
      "&.Mui-selected": {
        color: "#1A1E24",
        fontWeight: 600,
      },
      "&:hover": {
        color: "#1A1E24",
      },
    },
  },
  contentContainer: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "auto",
    minWidth: 0,
    padding: "20px 30px 0",
    minHeight: 0,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: "#555",
      },
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    padding: "20px 30px",
    borderTop: "1px solid #E1E6F1",
  },
}));

const FinstarClientProfJudgment = ({ open, onClose, clientId, clientName, isMfo }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [collateralMounted, setCollateralMounted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);

  const defaultTableRow = {
    borrowerName: "",
    creditProductType: "",
    limitAmount: "",
    creditTermFrom: "",
    creditTermTo: "",
    rateOrCommission: "",
    financialStatus: "",
    debtServiceQuality: "",
    loanQualityCategory: "",
    reservePercentage: "",
  };

  const defaultPledgorItem = {
    fullName: "",
    inn: "",
    registeredAddress: "",
    actualAddress: "",
    financialAssessment: "",
    formalAssessmentResult: "",
    negativeInfo: "",
  };

  const defaultGuarantorItem = {
    guarantorName: "",
    inn: "",
    actualAddress: "",
    bankAccountNumber: "",
    financialAssessment: "",
    formalAssessmentResult: "",
    negativeInfo: "",
    securityAmount: "",
    coverageAmount: "",
    acceptanceConclusion: "",
    qualityCategoryAssessment: "",
    rvpsConclusion: "",
  };

  const defaultCollateralItem = {
    pledgeSubject: "",
    pledgeDescription: "",
    pledgorNameInn: "",
    ownershipDocuments: "",
    locationAndStorage: "",
    verificationResults: "",
    marketValue: "",
    fairValue: "",
    discount: "",
    pledgeValue: "",
    fairValueDetermination: "",
    encumbranceInfo: "",
    creditAmount: "",
    interestAmount: "",
    commissionAmount: "",
    total: "",
    coveragePercentage: "",
    realizationAnalysis: "",
    qualityCategoryAssessment: "",
    rvpsFormation: "",
    realizableIn365Days: "",
    bankRightsImpossibility: "",
    rvpsConclusion: "",
    pledgeInsurance: "",
  };

  const [formData, setFormData] = useState({
    borrowerInfo: {
      inn: "",
      actualAddress: "",
      warehouseAddress: "",
      legalPremisesDocuments: "",
      actualPremisesDocuments: "",
      warehousePremisesDocuments: "",
      verificationDate: "",
      scopeOfActivity: "",
      industry: "",
      typeOfActivity: "",
      activityDescription: "",
      marketInformation: "",
      employeeCount: "",
    },
    existingObligations: [{ ...defaultTableRow }],
    totalLimit: [{ ...defaultTableRow }],
    collaterals: [{ ...defaultCollateralItem }],
    pledgors: [{ ...defaultPledgorItem }],
    guarantors: [{ ...defaultGuarantorItem }],
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      borrowerInfo: {
        ...prev.borrowerInfo,
        [field]: value,
      },
    }));
  };

  const handleTableCellChange = (tableType, rowIndex, fieldName, value) => {
    setFormData((prev) => {
      const updatedTable = [...prev[tableType]];
      updatedTable[rowIndex] = {
        ...updatedTable[rowIndex],
        [fieldName]: value,
      };
      return {
        ...prev,
        [tableType]: updatedTable,
      };
    });
  };

  const handleAddRow = (tableType) => {
    setFormData((prev) => ({
      ...prev,
      [tableType]: [...prev[tableType], { ...defaultTableRow }],
    }));
  };

  const handleDeleteRow = (tableType, rowIndex) => {
    setFormData((prev) => ({
      ...prev,
      [tableType]: prev[tableType].filter((_, index) => index !== rowIndex),
    }));
  };

  const handleCollateralItemChange = (colIndex, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.collaterals];
      updated[colIndex] = { ...updated[colIndex], [field]: value };
      return { ...prev, collaterals: updated };
    });
  };

  const handleAddCollateralColumn = () => {
    setFormData((prev) => ({
      ...prev,
      collaterals: [...prev.collaterals, { ...defaultCollateralItem }],
    }));
  };

  const handleDeleteCollateralColumn = (colIndex) => {
    setFormData((prev) => {
      if (prev.collaterals.length === 1) {
        const updated = [...prev.collaterals];
        updated[0] = { ...defaultCollateralItem };
        return { ...prev, collaterals: updated };
      }
      return { ...prev, collaterals: prev.collaterals.filter((_, i) => i !== colIndex) };
    });
  };

  const handlePledgorItemChange = (colIndex, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.pledgors];
      updated[colIndex] = { ...updated[colIndex], [field]: value };
      return { ...prev, pledgors: updated };
    });
  };

  const handleAddPledgorColumn = () => {
    setFormData((prev) => ({
      ...prev,
      pledgors: [...prev.pledgors, { ...defaultPledgorItem }],
    }));
  };

  const handleDeletePledgorColumn = (colIndex) => {
    setFormData((prev) => {
      if (prev.pledgors.length === 1) {
        const updated = [...prev.pledgors];
        updated[0] = { ...defaultPledgorItem };
        return { ...prev, pledgors: updated };
      }
      return { ...prev, pledgors: prev.pledgors.filter((_, i) => i !== colIndex) };
    });
  };

  const handleGuarantorItemChange = (colIndex, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.guarantors];
      updated[colIndex] = { ...updated[colIndex], [field]: value };
      return { ...prev, guarantors: updated };
    });
  };

  const handleAddGuarantorColumn = () => {
    setFormData((prev) => ({
      ...prev,
      guarantors: [...prev.guarantors, { ...defaultGuarantorItem }],
    }));
  };

  const handleDeleteGuarantorColumn = (colIndex) => {
    setFormData((prev) => {
      if (prev.guarantors.length === 1) {
        const updated = [...prev.guarantors];
        updated[0] = { ...defaultGuarantorItem };
        return { ...prev, guarantors: updated };
      }
      return { ...prev, guarantors: prev.guarantors.filter((_, i) => i !== colIndex) };
    });
  };

  const ensureOne = (arr, defaultItem) =>
    arr && arr.length > 0 ? arr : [{ ...defaultItem }];

  const loadData = async () => {
    if (!clientId) return;
    const key = getRandomKey();
    dispatch(AppActions.startLoading(key));
    try {
      const response = await getAPI().request({
        url: `/api/Finstar/client/${clientId}/professional-judgment/data`,
        method: "GET",
      });
      const data = response?.data ?? response;
      if (data) {
        setFormData((prev) => ({
          ...prev,
          ...data,
          existingObligations: ensureOne(data.existingObligations, defaultTableRow),
          totalLimit: ensureOne(data.totalLimit, defaultTableRow),
          collaterals: ensureOne(data.collaterals, defaultCollateralItem),
          pledgors: ensureOne(data.pledgors, defaultPledgorItem),
          guarantors: ensureOne(data.guarantors, defaultGuarantorItem),
        }));
      }
    } catch (error) {
      console.error("[FinstarClientProfJudgment] loadData", error);
      throw error; // Re-throw for polling to catch
    } finally {
      dispatch(AppActions.endLoading(key));
    }
  };

  useEffect(() => {
    if (!open || !clientId) return;

    const loadActivityTypes = async () => {
      try {
        const response = await getAPI().request({
          url: "/api/Finstar/activity-types",
          method: "GET",
        });
        if (response?.data) {
          setActivityTypes(response.data);
        }
      } catch (error) {
        console.error("[FinstarClientProfJudgment] loadActivityTypes", error);
      }
    };

    loadActivityTypes();
    loadData();
  }, [open, clientId]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 1) setCollateralMounted(true);
  };

  const hasAnyField = (item) =>
    Object.values(item).some((v) => v !== null && v !== undefined && v !== "");

  const handleSave = async () => {
    if (!clientId) return;
    const key = getRandomKey();
    setIsSaving(true);
    try {
      dispatch(AppActions.startLoading(key));
      const payload = {
        ...formData,
        existingObligations: formData.existingObligations.filter(hasAnyField),
        totalLimit: formData.totalLimit.filter(hasAnyField),
        collaterals: formData.collaterals.filter(hasAnyField),
        pledgors: formData.pledgors.filter(hasAnyField),
        guarantors: formData.guarantors.filter(hasAnyField),
      };
      await getAPI().request({
        url: `/api/Finstar/client/${clientId}/professional-judgment/data`,
        method: "PUT",
        data: payload,
      });
    } catch (error) {
      console.error("[FinstarClientProfJudgment] handleSave", error);
    } finally {
      setIsSaving(false);
      dispatch(AppActions.endLoading(key));
    }
  };

  const handleExport = async () => {
    if (!clientId) return;
    const key = getRandomKey();
    setIsExporting(true);
    try {
      dispatch(AppActions.startLoading(key));
      const fileSettings = { name: `professional-judgment-${clientId}.xlsx` };
      const blob = await getAPI().request({
        url: `/api/Finstar/client/${clientId}/professional-judgment/file`,
        method: "GET",
        responseType: "blob",
        _fileSettings: fileSettings,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileSettings.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[FinstarClientProfJudgment] handleExport", error);
    } finally {
      setIsExporting(false);
      dispatch(AppActions.endLoading(key));
    }
  };

  const tabs = [
    { label: t("finstar.borrowerInformation.title") },
    { label: "Залоги, поручительства" },
  ];

  return (
    <ModalWrapper
      maxWidth="90vw"
      open={open}
      handleClose={onClose}
      btnTxt="Close"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(90vh - 60px)",
          margin: "-30px",
          padding: 0,
        }}
      >
        {/* Header - fixed */}
        <Box sx={{ flexShrink: 0, padding: "30px 30px 0px" }}>
          <ModalTittle
            fontSize="20px"
            children2={`${t(isMfo ? "ProfessionalJudgmentMfo" : "finstar.professionalJudgment.title")} ${clientName || ""}`}
            marginBottom="10px"
          />
        </Box>

        {/* Tabs - fixed */}
        {tabs.length > 0 && (
          <Box className={classes.tabsContainer}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Tab content - scrollable */}
        <Box className={classes.contentContainer}>
          <Box sx={{ overflow: "hidden", height: selectedTab === 0 ? "auto" : 0 }}>
            <BorrowerInfoTab
              formData={formData}
              handleInputChange={handleInputChange}
              handleTableCellChange={handleTableCellChange}
              handleAddRow={handleAddRow}
              handleDeleteRow={handleDeleteRow}
              isMfo={isMfo}
              clientId={clientId}
              onSave={handleSave}
              onDataRefresh={loadData}
              activityTypes={activityTypes}
            />
          </Box>
          {collateralMounted && (
            <Box sx={{ overflow: "hidden", height: selectedTab === 1 ? "auto" : 0 }}>
              <CollateralTab
                collateralItems={formData.collaterals}
                onItemChange={handleCollateralItemChange}
                onAddColumn={handleAddCollateralColumn}
                onDeleteColumn={handleDeleteCollateralColumn}
                pledgorItems={formData.pledgors}
                onPledgorChange={handlePledgorItemChange}
                onPledgorAdd={handleAddPledgorColumn}
                onPledgorDelete={handleDeletePledgorColumn}
                guarantorItems={formData.guarantors}
                onGuarantorChange={handleGuarantorItemChange}
                onGuarantorAdd={handleAddGuarantorColumn}
                onGuarantorDelete={handleDeleteGuarantorColumn}
              />
            </Box>
          )}
        </Box>

        {/* Footer - fixed */}
        <Box className={classes.buttonContainer}>
            <div style={{ marginTop: '-20px' }}>
              <ButtonSave
                onClick={handleSave}
                marginTop={0}
                marginBottom={0}
              >
                buttons.Save
              </ButtonSave>
            </div>
          <ButtonBB onClick={handleExport} variant="outlined" disabled={isExporting}>
            {t("buttons.ExportToExcel")}
          </ButtonBB>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default FinstarClientProfJudgment;
